import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, UserMinus, UserPlus } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { formatAmount, formatDate, formatPercent } from "@/lib/format"
import {
  COST_BEHAVIOR_LABELS,
  PAYROLL_REGIME_LABELS,
  PERSON_NATURE_LABELS,
  type Person,
  type PersonNature,
} from "@/lib/types"
import { useTableKeyboardNav } from "@/lib/useTableKeyboardNav"
import { ayudaRegimen, etiquetaRegimen } from "@/lib/regimenes"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { PersonFormDialog, type PersonFormValues } from "@/routes/masters/PersonFormDialog"
import { BajaDePersonaDialog } from "@/routes/masters/BajaDePersonaDialog"
import { subirDocumento } from "@/routes/expediente/api"
import { fetchClients, fetchCostCenters, fetchPeople, savePerson } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

const NATURE_VARIANT: Record<PersonNature, "statutory" | "secondary" | "management"> = {
  costo_venta: "statutory",
  gasto_operativo: "secondary",
  capex: "management",
}

const NATURE_BAR: Record<PersonNature, string> = {
  costo_venta: "bg-book-statutory",
  gasto_operativo: "bg-muted-foreground",
  capex: "bg-book-management",
}

export function PeoplePage() {
  const { can } = useRbac()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState<Person | null>(null)
  const [creating, setCreating] = React.useState(false)
  const [dandoDeBaja, setDandoDeBaja] = React.useState<Person | null>(null)

  const peopleQuery = useQuery({ queryKey: ["people"], queryFn: fetchPeople })
  const costCentersQuery = useQuery({ queryKey: ["cost-centers"], queryFn: fetchCostCenters })
  const clientsQuery = useQuery({ queryKey: ["clients"], queryFn: fetchClients })

  const mutation = useMutation({
    mutationFn: (payload: { values: PersonFormValues; id?: string }) =>
      savePerson({
        data: {
          id: payload.id,
          fullName: payload.values.fullName,
          docNumber: payload.values.docNumber,
          position: payload.values.position,
          costCenterId: payload.values.costCenterId,
          nature: payload.values.nature,
          behavior: payload.values.behavior,
          regime: payload.values.regime,
          billable: payload.values.billable,
          clientIds: payload.values.clientIds,
          startDate: payload.values.startDate,
          endDate: payload.values.endDate,
          lineaHoja: payload.values.lineaHoja,
          email: payload.values.email,
          salaryBand: payload.values.salaryBand,
          monthlyGross: Number(payload.values.monthlyGross),
          plannedRaise: payload.values.plannedRaise || undefined,
        },
        reason: payload.values.reason,
      }),
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Persona guardada",
        description: "El payroll proyectado y el margen bruto por cliente se recalculan.",
      })
      setEditing(null)
      setCreating(false)
      void queryClient.invalidateQueries({ queryKey: ["people"] })
    },
    onError: onWriteError("la persona"),
  })

  /*
    LA BAJA. Cesar: «deberíamos poder darle de baja a una persona también y
    poner la fecha de cierre y su documentación de salida». Escribe la fecha de
    salida con motivo y, si hay archivo, lo archiva en el Expediente de su
    sociedad a nombre de la persona.
  */
  const baja = useMutation({
    mutationFn: async (d: { persona: Person; fechaSalida: string; motivo: string; archivo: File | null }) => {
      await savePerson({ data: { id: d.persona.id, endDate: d.fechaSalida, status: "baja" }, reason: d.motivo })
      if (d.archivo) {
        await subirDocumento({
          archivo: d.archivo, tipo: "otro",
          nombre: `Baja · ${d.persona.fullName} · ${d.fechaSalida}`,
          entityId: d.persona.entityId, nota: d.motivo,
        })
      }
    },
    onSuccess: (_r, d) => {
      toast({ variant: "success", title: "Baja registrada",
              description: d.archivo ? "La persona queda con su fecha de salida y el documento en el Expediente." : "La persona queda con su fecha de salida." })
      setDandoDeBaja(null)
      void queryClient.invalidateQueries({ queryKey: ["people"] })
    },
    onError: onWriteError("la baja"),
  })

  const people = peopleQuery.data ?? []
  const clients = clientsQuery.data ?? []
  const nav = useTableKeyboardNav({
    count: people.length,
    onActivate: (index) => {
      if (can("edit") && people[index]) setEditing(people[index])
    },
  })

  const summary = people.reduce<Record<PersonNature, { count: number; gross: number }>>(
    (acc, person) => {
      acc[person.nature].count += 1
      acc[person.nature].gross += person.monthlyGross
      return acc
    },
    {
      costo_venta: { count: 0, gross: 0 },
      gasto_operativo: { count: 0, gross: 0 },
      capex: { count: 0, gross: 0 },
    },
  )
  const totalGross = people.reduce((sum, person) => sum + person.monthlyGross, 0)

  const clientName = (id: string) => clients.find((client) => client.id === id)?.name ?? id

  return (
    <div className="space-y-3">
      {/* Composicion del costo del equipo por naturaleza: la consecuencia del maestro. */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-[24rem] flex-1 flex-wrap items-stretch divide-x divide-border rounded-md border bg-surface">
          {(Object.keys(PERSON_NATURE_LABELS) as PersonNature[]).map((nature) => {
            const share = totalGross ? summary[nature].gross / totalGross : 0
            return (
              <div key={nature} className="min-w-[10rem] flex-1 px-3 py-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="eyebrow">{PERSON_NATURE_LABELS[nature]}</span>
                  <span className="num text-2xs text-muted-foreground">
                    {formatPercent(share, 0)}
                  </span>
                </div>
                <div className="num mt-0.5 text-left text-md font-semibold">
                  {formatAmount(summary[nature].gross)}
                </div>
                <div className="text-2xs text-muted-foreground">
                  {summary[nature].count}{" "}{t("persona(s)")}</div>
                <div className="mt-1 h-[3px] w-full overflow-hidden rounded-full bg-border">
                  <div
                    className={cn("h-full", NATURE_BAR[nature])}
                    style={{ width: `${share * 100}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>

        <Button size="sm" disabled={!can("edit")} onClick={() => setCreating(true)}>
          <UserPlus />
          {t("Alta de persona")}</Button>
      </div>

      <WorkPanel
        title={t("Personas")}
        meta={
          <Badge variant="outline" mono>
            {people.length}
          </Badge>
        }
        footnote={t("Aqui se define quien es quien y cuanto gana. Las cargas (EsSalud, CTS, gratificaciones, SCTR o retencion de 4ta) las calcula la planilla con la norma de cada pais: ver Contabilidad › Planilla.")}
      >
        <DataState
          loading={peopleQuery.isLoading}
          error={peopleQuery.error}
          onRetry={() => void peopleQuery.refetch()}
          empty={people.length === 0}
          emptyMessage={t("Todavia no hay personas en el maestro.")}
          emptyHint={t("Sin personas no hay payroll proyectado ni margen bruto por cliente: son la mayor partida de costo de una empresa de servicios.")}
          emptyAction={
            <Button size="sm" disabled={!can("edit")} onClick={() => setCreating(true)}>
              <UserPlus />
              {t("Dar de alta a la primera persona")}</Button>
          }
          columns={[16, 8, 12, 12, 10, 8, 10, 6, 12, 6]}
        >
          <Table containerClassName="max-h-[calc(100vh-24rem)]">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[12rem]">{t("Persona")}</TableHead>
                {/*
                  LA SOCIEDAD, Y LA SEGUNDA COLUMNA. Cesar: «las personas
                  deberían estar asociadas a cada sociedad». El dato existía en
                  la base y no llegaba ni al listado, así que no había forma de
                  mirar el maestro y ver que alguien está donde no debe.

                  Va junto al nombre y no al final: es lo que hay que poder
                  cruzar con la persona de un vistazo.
                */}
                <TableHead className="w-44">{t("Sociedad")}</TableHead>
                <TableHead className="w-24">{t("DNI")}</TableHead>
                <TableHead className="w-40">{t("Cargo")}</TableHead>
                <TableHead className="w-36">{t("Area / centro de costo")}</TableHead>
                <TableHead className="w-[9.5rem]">{t("Naturaleza")}</TableHead>
                <TableHead className="w-[7.5rem]">{t("Comportamiento")}</TableHead>
                <TableHead className="w-[9.5rem]">{t("Regimen")}</TableHead>
                <TableHead className="w-20">{t("Facturable")}</TableHead>
                <TableHead className="w-44">{t("Clientes asignados")}</TableHead>
                <TableHead className="w-[5.5rem]">{t("Desde")}</TableHead>
                <TableHead className="w-[5.5rem]">{t("Hasta")}</TableHead>
                <TableHead className="w-16">{t("Banda")}</TableHead>
                <TableHead className="w-28 text-right">{t("Bruto mensual")}</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody {...nav.bodyProps}>
              {people.map((person, index) => (
                <TableRow
                  key={person.id}
                  {...nav.rowProps(index)}
                  className={cn("outline-none", nav.cursor === index && "row-cursor")}
                >
                  <TableCell className="truncate font-medium" title={person.fullName}>
                    {person.fullName}
                  </TableCell>
                  <TableCell className="code text-muted-foreground">{person.docNumber}</TableCell>
                  <TableCell
                    className="truncate text-2xs"
                    title={`${person.entityName}${person.entityCountry ? ` · ${person.entityCountry}` : ""}`}
                  >
                    <span className="truncate">{person.entityName}</span>
                    {person.entityCountry ? (
                      <span className="code ml-1.5 text-2xs text-muted-foreground">
                        {person.entityCountry.toUpperCase()}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="truncate" title={person.position}>
                    {person.position}
                  </TableCell>
                  <TableCell className="truncate text-2xs text-muted-foreground">
                    {person.costCenterName}
                  </TableCell>
                  <TableCell>
                    <Badge variant={NATURE_VARIANT[person.nature]} className="whitespace-nowrap">
                      {PERSON_NATURE_LABELS[person.nature]}
                    </Badge>
                  </TableCell>
                  <TableCell className="truncate text-2xs text-muted-foreground">
                    {COST_BEHAVIOR_LABELS[person.behavior]}
                  </TableCell>
                  {/*
                    EL REGIMEN, CON EL NOMBRE DE SU PAIS. `PAYROLL_REGIME_LABELS`
                    era peruano para las cuatro sociedades: una persona de São
                    Paulo salia como «Recibo por honorarios (4ta)», que es la
                    cuarta categoria de renta PERUANA. Ver `lib/regimenes.ts`.
                  */}
                  <TableCell
                    className="truncate text-2xs text-muted-foreground"
                    title={ayudaRegimen(person.regime, person.entityCountry)}
                  >
                    {etiquetaRegimen(person.regime, person.entityCountry)}
                  </TableCell>
                  <TableCell>
                    {person.billable ? (
                      <Badge variant="success-soft">si</Badge>
                    ) : (
                      <span className="text-2xs text-muted-foreground">no</span>
                    )}
                  </TableCell>
                  <TableCell
                    className="truncate text-2xs text-muted-foreground"
                    title={person.clientIds.map(clientName).join(", ")}
                  >
                    {person.clientIds.length === 0 ? "—" : person.clientIds.map(clientName).join(", ")}
                  </TableCell>
                  <TableCell className="code text-2xs text-muted-foreground">
                    {formatDate(person.startDate)}
                  </TableCell>
                  <TableCell className="code text-2xs text-muted-foreground">
                    {person.endDate ? formatDate(person.endDate) : "—"}
                  </TableCell>
                  <TableCell className="code text-2xs">{person.salaryBand}</TableCell>
                  <TableCell className="num-strong">{formatAmount(person.monthlyGross)}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {person.status === "activo" ? (
                      <Button size="xs" variant="ghost" disabled={!can("edit")}
                              className="opacity-70 transition-opacity group-hover/row:opacity-100"
                              onClick={() => setDandoDeBaja(person)} title={t("Dar de baja")}>
                        <UserMinus />
                      </Button>
                    ) : null}
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={!can("edit")}
                      className="opacity-70 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100"
                      onClick={() => setEditing(person)}
                    >
                      <Pencil />
                      {t("Editar")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12} className="text-2xs uppercase tracking-[0.07em]">
                  {t("Costo bruto mensual del equipo (sin cargas)")}</TableCell>
                <TableCell className="num">{formatAmount(totalGross)}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </DataState>
      </WorkPanel>

      <BajaDePersonaDialog
        persona={dandoDeBaja}
        onOpenChange={(v) => { if (!v) setDandoDeBaja(null) }}
        pending={baja.isPending}
        onConfirm={(d) => dandoDeBaja ? baja.mutateAsync({ persona: dandoDeBaja, ...d }).then(() => undefined) : undefined}
      />

      <PersonFormDialog
        open={Boolean(editing) || creating}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setCreating(false)
          }
        }}
        person={editing}
        costCenters={costCentersQuery.data ?? []}
        clients={clients}
        pending={mutation.isPending}
        onSubmit={(values) =>
          mutation.mutateAsync({ values, id: editing?.id }).then(() => undefined)
        }
      />
    </div>
  )
}
